import { useState } from "react";

import clsx from "clsx";
import { Link } from "react-router-dom";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { VideoToolTip } from "@components/video-tooltip";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { CreateBreakdownModal } from "@features/breakdown-v2/create-breakdown-modal";
import { CustomMetricsNewBadge } from "@features/custom-metrics/custom-metrics-modal";
import { useAvailableBreakdownConfigs } from "@features/sales-v3/hooks";
import { BreakdownButtonActive } from "@features/sales-v3/top-level-filters/components/breakdown-button-active";
import { BreakdownButtonSelect } from "@features/sales-v3/top-level-filters/components/breakdown-button-select";
import { EllipsisMenu } from "@features/shared";

import { useSalesPageV3NextContext } from "../sales-v3-next-provider";

import { FilterButtonActive } from "./filter-button/filter-button-active";
import { FilterButtonSelect } from "./filter-button/filter-button-select";

export const FilterBreakdownLabelBar = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { user } = useUser();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRange,
    dateRangeInterval,
    compareDateRange,
    breakdowns,
    filters,
    setBreakdowns,
    setFilters,
  } = useSalesPageV3NextContext();

  const [isCreateBreakdownModalOpen, setIsCreateBreakdownModalOpen] =
    useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const availableBreakdownConfigs = useAvailableBreakdownConfigs(breakdowns);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = breakdowns.findIndex(({ key }) => key === active.id);
      const newIndex = breakdowns.findIndex(({ key }) => key === over?.id);

      const newOrdering = arrayMove(breakdowns, oldIndex, newIndex);
      setBreakdowns(newOrdering);
    }
  };

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    dateRange,
    compareDateRange,
    breakdowns,
    filters,
  };

  return (
    <div
      className={clsx(
        "-mx-6 px-6 py-4 bg-cosmic-5 border-y border-gray",
        "text-xs flex justify-between",
      )}
    >
      <div className="flex flex-grow">
        <div className="flex items-center mr-2 font-bold min-w-max">
          <div>FILTER BY</div>
          <VideoToolTip
            videoUrl="https://www.youtube.com/embed/6weF10eVM7E?si=qBEiODOHMPVXjm9P"
            contentDetails={{
              title: "Filtering",
              content:
                "Filters impact both chart and table views, and help you refine your " +
                "data to make it more meaningful and accurate for analysis.",
              kbLink: "https://docs.northbeam.io/docs/sales-page",
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((e) => (
            <FilterButtonActive
              key={e.key}
              breakdown={e}
              onDelete={() => {
                const newFilters = filters.filter((v) => v.key !== e.key);
                prettyLogEventPage({
                  domain: "Top Level Filters",
                  action: "onClick",
                  additionalActionInfo: "Delete Page Filters",
                  data: {
                    newFilters,
                    pageContext,
                  },
                });
                setFilters(newFilters);
              }}
            />
          ))}
          <FilterButtonSelect
            breakdownConfigs={breakdownConfigs}
            breakdowns={filters}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Top Level Filters",
                action: "onChange",
                additionalActionInfo: "Change Page Filters",
                data: {
                  newFilters: value,
                  pageContext,
                },
              });
              setFilters(value);
            }}
          />
        </div>
      </div>
      <div className="flex items-center">
        <div className="flex items-center mr-2 font-bold min-w-max">
          <div>BREAKDOWN BY</div>
          <VideoToolTip
            videoUrl="https://www.youtube.com/embed/8jQksVYzBFE?si=5HwVedmaymDFtQ5S"
            contentDetails={{
              title: "Breakdowns",
              content:
                "Breakdowns allow you to segment and analyze data by creating nested tables " +
                "and chart views based on marketing tactics.",
              kbLink:
                "https://docs.northbeam.io/docs/saved-views-and-breakdowns",
            }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={handleDragEnd}
            autoScroll={false}
          >
            <SortableContext items={breakdowns.map(({ key }) => key)}>
              {breakdowns.map((e, idx) => (
                <BreakdownButtonActive
                  key={e.key}
                  breakdownConfigs={[
                    ...availableBreakdownConfigs,
                    breakdownConfigs.find((v) => v.key === e.key)!,
                  ].filter((e) => e)}
                  breakdown={e}
                  position={idx + 1}
                  showPosition={breakdowns.length > 1}
                  onChange={(newValue) => {
                    const newBreakdowns = breakdowns.map((v) =>
                      v.key === e.key ? newValue : v,
                    );
                    prettyLogEventPage({
                      domain: "Top Level Filters",
                      action: "onChange",
                      additionalActionInfo: "Change Page Breakdowns",
                      data: {
                        newBreakdowns,
                        pageContext,
                      },
                    });
                    setBreakdowns(newBreakdowns);
                  }}
                  onDelete={() => {
                    const newBreakdowns = breakdowns.filter(
                      (v) => v.key !== e.key,
                    );
                    prettyLogEventPage({
                      domain: "Top Level Filters",
                      action: "onChange",
                      additionalActionInfo: "Delete Page Breakdowns",
                      data: {
                        newBreakdowns,
                        pageContext,
                      },
                    });
                    setBreakdowns(newBreakdowns);
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
          {availableBreakdownConfigs.length > 0 && (
            <BreakdownButtonSelect
              breakdownConfigs={availableBreakdownConfigs}
              placeholder={breakdowns.length > 0 ? "Add..." : undefined}
              onChange={(value) => {
                const newBreakdowns = [...breakdowns, value];
                prettyLogEventPage({
                  domain: "Top Level Filters",
                  action: "onChange",
                  additionalActionInfo: "Add Page Breakdowns",
                  data: {
                    newBreakdowns,
                    pageContext,
                  },
                });
                setBreakdowns(newBreakdowns);
              }}
            />
          )}
          <EllipsisMenu>
            <>
              <EllipsisMenu.Item
                as="button"
                onClick={() => setIsCreateBreakdownModalOpen(true)}
              >
                Create a new breakdown
                <CustomMetricsNewBadge className="ml-2" />
              </EllipsisMenu.Item>
              <EllipsisMenu.Item
                as={Link}
                to={user.pathFromRoot("/breakdowns")}
              >
                Manage breakdowns
                <CustomMetricsNewBadge className="ml-2" />
              </EllipsisMenu.Item>
            </>
          </EllipsisMenu>
        </div>
      </div>
      {isCreateBreakdownModalOpen && (
        <CreateBreakdownModal
          onClose={() => setIsCreateBreakdownModalOpen(false)}
          isOpen={isCreateBreakdownModalOpen}
        />
      )}
    </div>
  );
};
